import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Building } from "lucide-react";
import { PROPERTY_TYPES, PROPERTY_SUB_TYPES } from "@/features/properties/services/properties";
import { USER_TYPES, type SignupFormData } from "./types";

const propertyTypes = PROPERTY_TYPES.map((t) => ({ value: t, label: t }));
const getSubTypes = (type: string) => PROPERTY_SUB_TYPES[type] || [];

interface Step4Props {
  formData: SignupFormData;
  onChange: (field: keyof SignupFormData, value: string) => void;
  onPropertyTypeChange: (value: string) => void;
}

export function Step4PropertyName({
  formData,
  onChange,
  onPropertyTypeChange,
}: Step4Props) {
  return (
    <Card className="border-border bg-gradient-accent">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Building className="h-5 w-5 text-primary" />
          Name Your Property
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="propertyType" className="text-foreground">
              Property Type
            </Label>
            <Select value={formData.propertyType} onValueChange={onPropertyTypeChange}>
              <SelectTrigger className="bg-background border-border text-foreground">
                <SelectValue placeholder="Select property type" />
              </SelectTrigger>
              <SelectContent>
                {propertyTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="propertySubType" className="text-foreground">
              Property Sub-Type
            </Label>
            <Select
              value={formData.propertySubType}
              onValueChange={(value) => onChange("propertySubType", value)}
              disabled={!formData.propertyType}
            >
              <SelectTrigger className="bg-background border-border text-foreground">
                <SelectValue placeholder="Select sub-type" />
              </SelectTrigger>
              <SelectContent>
                {getSubTypes(formData.propertyType).map((sub) => (
                  <SelectItem key={sub} value={sub}>
                    {sub}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="propertyName" className="text-foreground">
            Property Name
          </Label>
          <Input
            id="propertyName"
            value={formData.propertyName}
            onChange={(e) => onChange("propertyName", e.target.value)}
            className="bg-background border-border text-foreground"
            placeholder="e.g., Downtown Office Complex, Main Residence, etc."
          />
          <p className="text-sm text-muted-foreground mt-2">
            Choose a name that helps you identify this property in your dashboard
          </p>
        </div>

        <div className="bg-background rounded-lg p-4 border border-border">
          <h4 className="font-semibold text-foreground mb-3">Setup Summary:</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name:</span>
              <span className="text-foreground">
                {formData.firstName} {formData.lastName}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email:</span>
              <span className="text-foreground">{formData.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">User Type:</span>
              <span className="text-foreground">
                {USER_TYPES.find((t) => t.id === formData.userType)?.title}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Property Type:</span>
              <span className="text-foreground">
                {propertyTypes.find((t) => t.value === formData.propertyType)?.label ||
                  "Not selected"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Property Sub-Type:</span>
              <span className="text-foreground">
                {formData.propertyType
                  ? getSubTypes(formData.propertyType).find(
                      (s) => s === formData.propertySubType
                    ) || "Not selected"
                  : "Not selected"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Property Name:</span>
              <span className="text-foreground">
                {formData.propertyName || "Not named yet"}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
